import { useState, useEffect, useContext, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiCalendar, FiAlertCircle, FiX, FiChevronUp, FiChevronDown, FiCheck, FiX as FiXIcon, FiPackage, FiDollarSign } from 'react-icons/fi';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import UserContext from '../../Components/UserContext';
import ErrorBoundary from '../../Components/ErrorBoundary';
import { formatCurrency } from '../../utils/currencyFormatter';

function ConsultantPrograms() {
  const { session } = useContext(UserContext);
  const [programs, setPrograms] = useState([]);
  const [isEditing, setIsEditing] = useState(null);

  // Store slotPrices locally since backend doesn't return it
  // Use localStorage to persist across page refreshes
  const getSlotPricesCache = () => {
    try {
      const cached = localStorage.getItem('consultantProgramSlotPrices');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  };

  const setSlotPricesCache = (updater) => {
    const current = getSlotPricesCache();
    const updated = typeof updater === 'function' ? updater(current) : updater;
    try {
      localStorage.setItem('consultantProgramSlotPrices', JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save slotPrices to localStorage:', e);
    }
    return updated;
  };

  const [slotPricesCache, setSlotPricesCacheState] = useState(getSlotPricesCache());

  // Wrapper to update both state and localStorage
  const updateSlotPricesCache = (updater) => {
    const updated = setSlotPricesCache(updater);
    setSlotPricesCacheState(updated);
  };

  // Get services - only consultant services
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';

  // Filter to only consultant services
  const consultantServices = trainerServices.filter(service =>
    (servicePartnerTypes[service] || 'instructor') === 'consultant'
  );

  const [newProgram, setNewProgram] = useState({
    name: '',
    description: '',
    effectiveDate: '',
    isActive: true,
    service: activeService || '',
    price: {}, // Object mapping slot durations to prices: { "30 mins": 500, "1 hour": 1000 }
    // Consultant-specific fields
    timeWindowStart: '09:00',
    timeWindowEnd: '17:00',
    slotDurations: ['1 hour']
  });
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [dateTimeSettings, setDateTimeSettings] = useState({
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12-hour'
  });
  const [filters, setFilters] = useState({
    status: 'All',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });
  const [isPWA, setIsPWA] = useState(false);
  const dateInputRef = useRef(null);
  const editFormRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await HttpInterceptor.get('/api/trainer-settings');
        setDateTimeSettings({
          dateFormat: response.data.data.dateFormat || 'MM/DD/YYYY',
          timeFormat: response.data.data.timeFormat || '12-hour'
        });
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      }
    };

    if (session?.id) {
      fetchPrograms();
      fetchSettings();
    }
  }, [session]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (dateTimeSettings.dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      default: return dateString;
    }
  };

  const handleManualDateChange = (e) => {
    const value = e.target.value;
    let date;

    if (dateTimeSettings.dateFormat === 'DD/MM/YYYY') {
      const [day, month, year] = value.split('/');
      if (day && month && year) {
        date = new Date(`${year}-${month}-${day}`);
      }
    } else if (dateTimeSettings.dateFormat === 'MM/DD/YYYY') {
      const [month, day, year] = value.split('/');
      if (month && day && year) {
        date = new Date(`${year}-${month}-${day}`);
      }
    } else if (dateTimeSettings.dateFormat === 'YYYY-MM-DD') {
      date = new Date(value);
    }

    if (date && !isNaN(date.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate >= today) {
        setNewProgram({ ...newProgram, effectiveDate: date.toISOString().split('T')[0] });
      } else {
        toast.error('Start date cannot be in the past. Please select today or a future date.');
        setNewProgram({ ...newProgram, effectiveDate: today.toISOString().split('T')[0] });
      }
    } else {
      setNewProgram({ ...newProgram, effectiveDate: value });
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (dateTimeSettings.dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      default: return date.toISOString().split('T')[0];
    }
  };

  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await HttpInterceptor.get('/api/programs', {
        params: {
          trainerId: session?.id,
          service: activeService // Filter by consultant service
        }
      });
      // Filter to only consultant programs (those with timeWindowStart/timeWindowEnd)
      const consultantPrograms = data.filter(program =>
        program.timeWindowStart && program.timeWindowEnd
      );

      // Merge slotPrices from cache with fetched programs
      const cache = getSlotPricesCache();
      const programsWithSlotPrices = consultantPrograms.map(program => ({
        ...program,
        slotPrices: cache[program._id] || program.slotPrices || {}
      }));

      setPrograms(programsWithSlotPrices);
    } catch (err) {
      setError(err);
      console.error('Fetch programs error:', err);
      if (err.response?.status === 401) {
        toast.error('Please log in to view programs');
      } else if (err.response?.status === 500) {
        toast.error('We are having trouble loading programs right now. Please try again in a moment.');
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch programs';
        toast.error(`Failed to fetch programs: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    // Validation for consultant programs
    if (!newProgram.name || !newProgram.name.trim()) {
      toast.error('Please enter a program name');
      return;
    }

    if (!newProgram.effectiveDate || newProgram.effectiveDate.trim() === '') {
      toast.error('Please select a start date');
      return;
    }

    if (!newProgram.timeWindowStart || !newProgram.timeWindowEnd) {
      toast.error('Please set time window for consultation programs');
      return;
    }

    if (!newProgram.slotDurations || newProgram.slotDurations.length === 0) {
      toast.error('Please select at least one slot duration');
      return;
    }

    if (!newProgram.service || !newProgram.service.trim()) {
      toast.error('Please select a service');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Ensure effectiveDate is in YYYY-MM-DD format
      let formattedEffectiveDate = newProgram.effectiveDate;
      if (!formattedEffectiveDate || formattedEffectiveDate.trim() === '') {
        toast.error('Please select a start date');
        setIsLoading(false);
        return;
      }

      // Trim whitespace
      formattedEffectiveDate = formattedEffectiveDate.trim();

      // If it's already in ISO format (YYYY-MM-DD), use it
      if (formattedEffectiveDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in correct format, validate it's a valid date
        const testDate = new Date(formattedEffectiveDate);
        if (isNaN(testDate.getTime())) {
          toast.error('Invalid date. Please select a valid date.');
          setIsLoading(false);
          return;
        }
      } else {
        // Try to parse the date and convert to ISO format
        // Handle different input formats
        let date;
        if (formattedEffectiveDate.includes('/')) {
          // Handle MM/DD/YYYY or DD/MM/YYYY format
          const parts = formattedEffectiveDate.split('/');
          if (parts.length === 3) {
            if (dateTimeSettings.dateFormat === 'MM/DD/YYYY') {
              // MM/DD/YYYY
              date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
            } else if (dateTimeSettings.dateFormat === 'DD/MM/YYYY') {
              // DD/MM/YYYY
              date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            } else {
              date = new Date(formattedEffectiveDate);
            }
          } else {
            date = new Date(formattedEffectiveDate);
          }
        } else {
          date = new Date(formattedEffectiveDate);
        }

        if (!isNaN(date.getTime())) {
          formattedEffectiveDate = date.toISOString().split('T')[0];
        } else {
          toast.error('Invalid date format. Please select a valid date using the date picker.');
          setIsLoading(false);
          return;
        }
      }

      // Prepare program data for consultant programs
      // Convert slotPrices object to ensure all values are numbers (not strings)
      const slotPrices = {};
      if (newProgram.price && typeof newProgram.price === 'object') {
        Object.keys(newProgram.price).forEach(duration => {
          const priceValue = newProgram.price[duration];
          if (priceValue !== '' && priceValue !== null && priceValue !== undefined) {
            slotPrices[duration] = Number(priceValue);
          }
        });
      }

      const programData = {
        name: newProgram.name.trim(),
        description: newProgram.description || '',
        effectiveDate: formattedEffectiveDate,
        // Price is now an object mapping slot durations to prices
        slotPrices: slotPrices,
        // Keep price field for backwards compatibility (use first available price or 0)
        price: Object.keys(slotPrices).length > 0
          ? Number(Object.values(slotPrices)[0] || 0)
          : 0,
        service: newProgram.service.trim(),
        isActive: newProgram.isActive !== undefined ? newProgram.isActive : true,
        // Required fields for API validation (not used for consultant programs but required by backend)
        duration: Number(1),
        durationUnit: 'weeks',
        classesPerDuration: Number(1),
        // Consultant-specific fields
        timeWindowStart: newProgram.timeWindowStart,
        timeWindowEnd: newProgram.timeWindowEnd,
        slotDurations: newProgram.slotDurations
      };

      // Final validation before sending
      if (!programData.name || programData.name.trim() === '') {
        toast.error('Program name is required');
        setIsLoading(false);
        return;
      }
      if (!programData.effectiveDate || programData.effectiveDate.trim() === '') {
        toast.error('Start date is required');
        setIsLoading(false);
        return;
      }
      if (!programData.duration || !programData.durationUnit || programData.classesPerDuration === undefined) {
        toast.error('Duration fields are required');
        setIsLoading(false);
        return;
      }

      console.log('Sending program data:', JSON.stringify(programData, null, 2));

      if (isEditing !== null) {
        const { data } = await HttpInterceptor.put(`/api/programs/${isEditing}`, programData);
        console.log('Updated program response:', data);

        // Store slotPrices in cache since backend doesn't return it
        updateSlotPricesCache(prev => ({
          ...prev,
          [isEditing]: slotPrices
        }));

        const { data: updatedPrograms } = await HttpInterceptor.get('/api/programs', {
          params: {
            trainerId: session?.id,
            service: activeService
          }
        });
        console.log('Fetched programs after update:', updatedPrograms);
        const consultantPrograms = updatedPrograms.filter(program =>
          program.timeWindowStart && program.timeWindowEnd
        );

        // Merge slotPrices from cache with program data
        const cache = getSlotPricesCache();
        const programsWithSlotPrices = consultantPrograms.map(program => ({
          ...program,
          slotPrices: cache[program._id] || program.slotPrices || {}
        }));

        // Log each program's slotPrices to debug
        programsWithSlotPrices.forEach(prog => {
          console.log(`Program ${prog.name} - slotPrices:`, prog.slotPrices, 'price:', prog.price);
        });
        setPrograms(programsWithSlotPrices);
        toast.success('Consultation program updated successfully');
      } else {
        const { data } = await HttpInterceptor.post('/api/programs', programData);

        // Store slotPrices in cache for new program
        if (data && data._id) {
          updateSlotPricesCache(prev => ({
            ...prev,
            [data._id]: slotPrices
          }));

          // Merge slotPrices with the response data
          const programWithSlotPrices = {
            ...data,
            slotPrices: slotPrices
          };
          setPrograms([...programs, programWithSlotPrices]);
        } else {
          setPrograms([...programs, data]);
        }
        toast.success('Consultation program added successfully');
      }
      resetForm();
    } catch (err) {
      setError(err);
      const errorMsg = err.response?.data?.message || 'Operation failed. Please try again.';
      toast.error(errorMsg);
      console.error('Add/Edit program error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      if (err.response?.data?.errors) {
        console.error('Validation errors:', err.response.data.errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (program) => {
    const slotDurations = program.slotDurations || ['1 hour'];

    // Get slotPrices from cache first (since backend doesn't return it)
    const cache = getSlotPricesCache();
    const cachedSlotPrices = cache[program._id];

    // Handle both old format (single price) and new format (slotPrices object)
    let priceObject = {};
    if (cachedSlotPrices && typeof cachedSlotPrices === 'object' && !Array.isArray(cachedSlotPrices)) {
      // Use cached slotPrices - preserve all values as-is
      Object.keys(cachedSlotPrices).forEach(key => {
        const value = cachedSlotPrices[key];
        // Only include if value is valid (not null, undefined, or empty string)
        if (value !== null && value !== undefined && value !== '') {
          priceObject[key] = value;
        }
      });
    } else if (program.slotPrices && typeof program.slotPrices === 'object' && !Array.isArray(program.slotPrices)) {
      // Fallback to program.slotPrices if cache doesn't have it
      Object.keys(program.slotPrices).forEach(key => {
        const value = program.slotPrices[key];
        if (value !== null && value !== undefined && value !== '') {
          priceObject[key] = value;
        }
      });
    } else if (program.price !== undefined && program.price !== null && program.price !== '' && typeof program.price !== 'object') {
      // Old format: single price - don't apply to all, let user set individually
      // This prevents overwriting with old single price format
    }

    // Ensure all slot durations have a price entry (even if empty)
    slotDurations.forEach(duration => {
      if (!(duration in priceObject)) {
        priceObject[duration] = '';
      }
    });

    setNewProgram({
      name: program.name || '',
      description: program.description || '',
      effectiveDate: program.effectiveDate ? (typeof program.effectiveDate === 'string' ? program.effectiveDate.split('T')[0] : new Date(program.effectiveDate).toISOString().split('T')[0]) : '',
      isActive: program.isActive !== undefined ? program.isActive : true,
      service: program.service || activeService || '',
      price: priceObject,
      // Consultant-specific fields
      timeWindowStart: program.timeWindowStart || '09:00',
      timeWindowEnd: program.timeWindowEnd || '17:00',
      slotDurations: slotDurations
    });
    setIsEditing(program._id);
    setShowForm(true);

    setTimeout(() => {
      editFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const checkDependencies = async (programId) => {
    try {
      const response = await HttpInterceptor.get(`/api/programs/${programId}/dependencies`);
      return response.data;
    } catch (error) {
      console.error('Error checking dependencies:', error);
      return null;
    }
  };

  const handleDeleteClick = async (id) => {
    const dependencies = await checkDependencies(id);
    if (dependencies && dependencies.hasDependencies) {
      setDeleteDependencies(dependencies);
      setShowDeleteConfirm(id);
      return;
    }
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async (id, force = false) => {
    try {
      setIsLoading(true);
      const url = force ? `/api/programs/${id}?force=true` : `/api/programs/${id}`;
      await HttpInterceptor.delete(url);
      setPrograms(programs.filter(p => p._id !== id));
      toast.success('Consultation program deleted successfully');
      setShowDeleteConfirm(null);
      setDeleteDependencies(null);
      setShowForceDeleteConfirm(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete program. Please try again.';
      if (err.response?.data?.dependencies) {
        setDeleteDependencies({
          hasDependencies: true,
          dependencies: err.response.data.dependencies,
          message: err.response.data.message
        });
      } else {
        toast.error(errorMsg);
      }
      console.error('Delete program error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActiveStatus = async (id) => {
    try {
      setIsLoading(true);
      const program = programs.find(p => p._id === id);

      const { data } = await HttpInterceptor.patch(`/api/programs/${id}/toggle-status`, {
        isActive: !program.isActive
      });
      setPrograms(programs.map(p => p._id === id ? data : p));
      toast.success(`Consultation program ${!program.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update status. Please try again.';
      toast.error(errorMsg);
      console.error('Toggle status error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setNewProgram({
      name: '',
      description: '',
      effectiveDate: '',
      isActive: true,
      service: activeService || '',
      price: {},
      timeWindowStart: '09:00',
      timeWindowEnd: '17:00',
      slotDurations: ['1 hour']
    });
    setIsEditing(null);
    setShowForm(false);
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredItems = () => {
    let filtered = [...programs];

    if (filters.status !== 'All') {
      filtered = filtered.filter(program =>
        filters.status === 'Active' ? program.isActive : !program.isActive
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(program =>
        program.name.toLowerCase().includes(searchTerm) ||
        (program.description && program.description.toLowerCase().includes(searchTerm))
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  const sortedAndFilteredPrograms = getSortedAndFilteredItems();

  return (
    <ErrorBoundary>
      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
        {/* Hero Section */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} rounded-2xl ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'} overflow-hidden`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
                  <FiPackage className={isPWA ? 'text-orange-600' : 'text-white'} />
                  Consultation Programs
                </h1>
                <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'}`}>Manage your consultation programs</p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl' : 'bg-white text-orange-600 hover:bg-orange-50'} px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'}`}
              >
                <FiPlus size={20} />
                Add Program
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} p-4 sm:p-6 mb-6`}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search programs..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-4 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
              />
            </div>
            <div className="sm:w-48">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-4 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
              >
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div ref={editFormRef} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} p-6 ${isPWA ? 'rounded-2xl' : ''} mb-8`}>
            <h2 className={`text-lg font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-4`}>
              {isEditing !== null ? 'Edit Consultation Program' : 'Add New Consultation Program'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Service*</label>
                <select
                  value={newProgram.service}
                  onChange={(e) => setNewProgram({ ...newProgram, service: e.target.value })}
                  className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                  required
                >
                  <option value="">Select Service</option>
                  {consultantServices.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Program Name*</label>
                <input
                  type="text"
                  value={newProgram.name}
                  onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                  placeholder={
                    newProgram.service
                      ? `e.g. ${newProgram.service} Consultation`
                      : 'e.g. Numerology Consultation, Astrology Consultation, etc.'
                  }
                  className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                  required
                />
              </div>

              <div className="relative">
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                  Start Date* (Format: {dateTimeSettings.dateFormat})
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatDateForInput(newProgram.effectiveDate)}
                    onChange={handleManualDateChange}
                    placeholder={dateTimeSettings.dateFormat}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    ref={dateInputRef}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = new Date().toISOString().split('T')[0];
                      if (selectedDate >= today) {
                        setNewProgram({ ...newProgram, effectiveDate: selectedDate });
                      } else {
                        toast.error('Start date cannot be in the past. Please select today or a future date.');
                      }
                    }}
                  />
                  <div
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                    onClick={() => dateInputRef.current?.showPicker()}
                  >
                    <FiCalendar className={isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-orange-600'} />
                  </div>
                </div>
                <p className={`mt-1 text-xs ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>Select today or a future date</p>
              </div>

            </div>

            {/* Consultant-specific fields - Always shown */}
            <div className="border-t pt-4 mt-4">
              <h3 className={`text-md font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-4`}>Appointment Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Time Window Start*</label>
                  <input
                    type="time"
                    value={newProgram.timeWindowStart}
                    onChange={(e) => setNewProgram({ ...newProgram, timeWindowStart: e.target.value })}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Time Window End*</label>
                  <input
                    type="time"
                    value={newProgram.timeWindowEnd}
                    onChange={(e) => setNewProgram({ ...newProgram, timeWindowEnd: e.target.value })}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-2`}>Available Slot Durations*</label>
                  <div className="space-y-3">
                    {['30 mins', '45 mins', '1 hour', '1.5 hours', '2 hours', '3 hours'].map((duration) => {
                      const isSelected = newProgram.slotDurations.includes(duration);
                      const currentPrice = newProgram.price && typeof newProgram.price === 'object' ? (newProgram.price[duration] || '') : '';

                      return (
                        <div key={duration} className={`border ${isPWA ? 'border-orange-200' : 'border-gray-200'} rounded-lg p-3 ${isSelected ? (isPWA ? 'bg-orange-50/50' : 'bg-gray-50') : 'bg-white/50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewProgram({
                                      ...newProgram,
                                      slotDurations: [...newProgram.slotDurations, duration],
                                      price: {
                                        ...(newProgram.price || {}),
                                        [duration]: newProgram.price?.[duration] || ''
                                      }
                                    });
                                  } else {
                                    const updatedPrice = { ...(newProgram.price || {}) };
                                    delete updatedPrice[duration];
                                    setNewProgram({
                                      ...newProgram,
                                      slotDurations: newProgram.slotDurations.filter(d => d !== duration),
                                      price: updatedPrice
                                    });
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className={`text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'}`}>{duration}</span>
                            </label>
                          </div>
                          {isSelected && (
                            <div className="ml-6 mt-2">
                              <label className={`block text-xs font-medium ${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-1`}>
                                Price for {duration}
                              </label>
                              <div className="relative">
                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                                  ₹
                                </div>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={currentPrice === '' || currentPrice === null || currentPrice === undefined ? '' : currentPrice}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value) || 0);
                                    setNewProgram({
                                      ...newProgram,
                                      price: {
                                        ...(newProgram.price || {}),
                                        [duration]: value
                                      }
                                    });
                                  }}
                                  placeholder="0"
                                  className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                                />
                              </div>
                              {currentPrice && (
                                <p className={`mt-1 text-xs ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>
                                  Price: {formatCurrency(currentPrice)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className={`mt-2 text-xs ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>Select at least one slot duration and set prices for each</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAdd}
                disabled={
                  !newProgram.name ||
                  !newProgram.effectiveDate ||
                  !newProgram.timeWindowStart ||
                  !newProgram.timeWindowEnd ||
                  !newProgram.slotDurations?.length ||
                  isLoading
                }
                className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700'} text-white px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} ${(!newProgram.name ||
                    !newProgram.effectiveDate ||
                    !newProgram.timeWindowStart ||
                    !newProgram.timeWindowEnd ||
                    !newProgram.slotDurations?.length ||
                    isLoading) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isLoading ? 'Processing...' : isEditing !== null ? 'Update Program' : 'Add Program'}
              </button>

              <button
                onClick={resetForm}
                disabled={isLoading}
                className={`${isPWA ? 'text-orange-700 hover:text-orange-900 hover:bg-orange-50' : 'text-gray-600 hover:text-gray-800'} px-4 py-2 rounded-lg transition-colors ${isPWA ? 'font-medium' : ''}`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Programs List */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} overflow-hidden`}>
          {isLoading && programs.length === 0 ? (
            <div className="text-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-4 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading consultation programs...</p>
            </div>
          ) : sortedAndFilteredPrograms.length > 0 ? (
            <div className="p-4 sm:p-6">
              <div className={`mb-4 flex flex-wrap items-center gap-2 text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                <span className="font-medium">Sort by:</span>
                <button
                  onClick={() => requestSort('name')}
                  className={`px-3 py-1 rounded-md transition-all duration-200 ${sortConfig.key === 'name'
                      ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'hover:bg-orange-50 text-orange-600' : 'hover:bg-gray-100'
                    }`}
                >
                  Name
                  {sortConfig.key === 'name' && (
                    sortConfig.direction === 'ascending' ?
                      <FiChevronUp className="inline ml-1" size={14} /> :
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
                <button
                  onClick={() => requestSort('effectiveDate')}
                  className={`px-3 py-1 rounded-md transition-all duration-200 ${sortConfig.key === 'effectiveDate'
                      ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'hover:bg-orange-50 text-orange-600' : 'hover:bg-gray-100'
                    }`}
                >
                  Start Date
                  {sortConfig.key === 'effectiveDate' && (
                    sortConfig.direction === 'ascending' ?
                      <FiChevronUp className="inline ml-1" size={14} /> :
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
              </div>

              <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                {sortedAndFilteredPrograms.map((program) => (
                  <div key={program._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-lg hover:shadow-xl' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl p-5 transition-all duration-200 flex flex-col ${isPWA ? 'backdrop-blur-sm' : ''}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 pr-3">
                        <h3 className={`text-lg font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-1`}>{program.name}</h3>
                        {program.description && (
                          <p className={`text-sm ${isPWA ? 'text-orange-700/80' : 'text-gray-600'} line-clamp-2 mt-1`}>{program.description}</p>
                        )}
                      </div>
                    </div>

                    <div className={`mb-4 pb-4 border-b ${isPWA ? 'border-orange-200/50' : 'border-gray-100'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isPWA ? 'text-orange-700' : 'text-gray-700'}`}>Status:</span>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${program.isActive
                              ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 shadow-sm' : 'bg-green-100 text-green-800'
                              : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 shadow-sm' : 'bg-red-100 text-red-800'
                            }`}>
                            {program.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-medium transition-colors ${program.isActive ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                            Off
                          </span>
                          <button
                            onClick={() => !isLoading && toggleActiveStatus(program._id)}
                            disabled={isLoading}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${program.isActive
                                ? 'bg-orange-600'
                                : 'bg-gray-300'
                              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${program.isActive ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            >
                              {program.isActive ? (
                                <FiCheck className="h-3 w-3 text-orange-600 m-0.5" />
                              ) : (
                                <FiXIcon className="h-3 w-3 text-gray-400 m-0.5" />
                              )}
                            </span>
                          </button>
                          <span className={`text-xs font-medium transition-colors ${!program.isActive ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                            On
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4 flex-grow">
                      <div className={`flex items-center text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                        <FiClock className={`mr-2 ${isPWA ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0`} size={16} />
                        <span className="font-medium mr-1">Time Window:</span>
                        <span>{program.timeWindowStart} - {program.timeWindowEnd}</span>
                      </div>
                      <div className={`flex items-center text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                        <FiCalendar className={`mr-2 ${isPWA ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0`} size={16} />
                        <span className="font-medium mr-1">Start Date:</span>
                        <span>{formatDateDisplay(program.effectiveDate)}</span>
                      </div>
                      {program.slotDurations && program.slotDurations.length > 0 && (
                        <div className={`flex items-start text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                          <FiClock className={`mr-2 ${isPWA ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0 mt-0.5`} size={16} />
                          <div className="flex-1">
                            <span className="font-medium mr-1">Slot Durations & Prices:</span>
                            <div className="mt-1 space-y-1">
                              {program.slotDurations.map((duration) => {
                                // Handle both new format (slotPrices) and old format (single price)
                                const slotPrices = (program.slotPrices && typeof program.slotPrices === 'object' && !Array.isArray(program.slotPrices))
                                  ? program.slotPrices
                                  : {};
                                // Only use slotPrices for this specific duration, don't fall back to single price
                                const price = (slotPrices && slotPrices[duration] !== undefined && slotPrices[duration] !== null && slotPrices[duration] !== '')
                                  ? slotPrices[duration]
                                  : null;

                                return (
                                  <div key={duration} className={`flex items-center justify-between ${isPWA ? 'bg-orange-50/50' : 'bg-gray-50'} rounded px-2 py-1`}>
                                    <span className="text-xs">{duration}</span>
                                    {price !== null && price !== undefined && price !== '' ? (
                                      <span className="font-semibold text-xs">{formatCurrency(price)}</span>
                                    ) : (
                                      <span className="text-xs text-gray-400">No price set</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={`flex gap-2 pt-3 border-t ${isPWA ? 'border-orange-200/50' : 'border-gray-100'} mt-auto`}>
                      <button
                        onClick={() => handleEdit(program)}
                        disabled={isLoading}
                        className={`flex-1 flex items-center justify-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg' : 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'} px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      >
                        <FiEdit2 size={16} />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(program._id)}
                        disabled={isLoading}
                        className={`flex-1 flex items-center justify-center gap-2 ${isPWA ? 'text-red-600 hover:text-red-800 hover:bg-red-50' : 'text-red-600 hover:text-red-800 hover:bg-red-50'} px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      >
                        <FiTrash2 size={16} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <FiPackage className={`${isPWA ? 'text-orange-400' : 'text-gray-400'} mx-auto mb-4`} size={48} />
              <h3 className={`text-lg font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-2`}>No consultation programs created yet</h3>
              <p className={`${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-4`}>Get started by adding your first consultation program</p>
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className={`flex items-center gap-2 mx-auto ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700'} text-white px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'}`}
              >
                <FiPlus size={20} />
                Add Your First Program
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FiAlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Consultation Program</h3>
                </div>
              </div>

              {deleteDependencies && deleteDependencies.hasDependencies ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {deleteDependencies.message || 'This program has dependencies that will be affected:'}
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    {deleteDependencies.dependencies?.appointments > 0 && (
                      <li>{deleteDependencies.dependencies.appointments} appointment(s)</li>
                    )}
                  </ul>
                  <p className="text-sm text-red-600 mt-3 font-medium">
                    Are you sure you want to delete this program? This action cannot be undone.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this consultation program? This action cannot be undone.
                </p>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    setDeleteDependencies(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                {deleteDependencies && deleteDependencies.hasDependencies && !showForceDeleteConfirm ? (
                  <button
                    onClick={() => setShowForceDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Force Delete
                  </button>
                ) : (
                  <button
                    onClick={() => confirmDelete(showDeleteConfirm, showForceDeleteConfirm)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default ConsultantPrograms;

